import { AppearanceProvider } from '@/containers/appearance';
import { TeamInvitePrompt } from '@/containers/onboarding/team-invite-dialog.component';
import { useAppSumoUpgrade } from '@/hooks/use-appsumo-upgrade.hook';
import { useIntercomBoot } from '@/hooks/use-intercom.hook';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectAiChatOpen,
  setAiChatInputValue,
  setAiChatOpen,
  toggleAiChat,
} from '@/store/features/ui.feature';
import { useUser } from '@clerk/react';
import { useNavigate } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { lazy, Suspense, useEffect, type ComponentType } from 'react';
import { AgentChatErrorBoundary } from './agent-chat/agent-chat-error-boundary.component';
import { AiSidebarFallback } from './ai-sidebar/ai-sidebar-fallback.component';
import {
  CopilotRuntimeProvider,
  useCopilotRuntime,
  useIsCopilotRoute,
  useIsHomeRoute,
} from './agent-chat/copilot-runtime-provider.component';
import { AppEffects } from './app-effects';
import { AppSumoUpgradeBanner } from './appsumo/appsumo-upgrade-banner.component';
import { CallProvider } from '@/containers/calling/call-provider.component';
import { CreditGateDialog } from './billing/credit-gate-dialog.component';
import { InvitationPrompt } from './invitation-prompt.component';
import { AgentProfileReadyDialog } from './agents/agent-profile-ready-dialog.component';
import { G2ReviewBanner } from './g2-review-banner.component';
import { ProductHuntRewardBanner } from './producthunt-reward-banner.component';
import { ReviewRewardBanner } from './review-reward-banner.component';
import { TrialOfferBanner } from './trial/trial-offer-banner.component';
import { TrialStatusBanner } from './trial/trial-status-banner.component';
import { SidebarProvider } from './sidebar/sidebar-provider.component';
import { TabsSidebar } from './sidebar/tabs/tabs-sidebar.component';

const loadAiSidebar = () => import('./ai-sidebar/ai-sidebar.component');

/**
 * DEV only: the AppSumo preview panel (persona and phase in one click).
 * `import.meta.env.DEV` is false in a production build, so this import is
 * dead code there and the panel's module never ships.
 */
const AppSumoPreviewPanel = import.meta.env.DEV
  ? lazy(() =>
      import('./appsumo/appsumo-preview-panel.component').then((m) => ({
        default: m.AppSumoPreviewPanel,
      }))
    )
  : null;

// The upgrade prompts and the Free nudge, loaded on their own. A chunk that
// fails to load (a stale deploy) leaves no prompt rather than breaking the
// shell.
const UpgradePromptHost = lazy(
  (): Promise<{ default: ComponentType }> =>
    import('@/components/upgrade/upgrade-prompt-host.component')
      .then((m) => ({ default: m.UpgradePromptHost }))
      .catch(() => ({ default: () => null }))
);

const AiSidebar = lazy(() =>
  loadAiSidebar().then((m) => ({ default: m.AiSidebar }))
);

/** Warm the dock's chunk on idle so the first open doesn't wait on a fetch. */
function usePrefetchAiSidebar() {
  useEffect(() => {
    // A warm-up that misses (a stale chunk after a deploy) is only a warm-up:
    // vite:preloadError already handles the reload, and the dock's own lazy
    // import tries again on open. Uncaught, each miss was an unhandled
    // rejection (10 in one Replay session).
    const warm = () => void loadAiSidebar().catch(() => {});
    // requestIdleCallback is still missing on older Safari.
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(warm);
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(warm, 2000);
    return () => clearTimeout(id);
  }, []);
}

/**
 * ⌘J toggles the dock, except on home and /clu where the dock can't
 * mount: there it starts a new chat on the full-screen Copilot instead.
 * Rendered inside <CopilotRuntimeProvider> because it needs the runtime.
 */
function CopilotHotkey() {
  const dispatch = useAppDispatch();
  const posthog = usePostHog();
  const { user } = useUser();
  const hasCompletedOnboarding = !!user?.unsafeMetadata?.completed_onboarding;
  const aiChatOpen = useAppSelector(selectAiChatOpen);
  const onCopilotRoute = useIsCopilotRoute();
  const onHomeRoute = useIsHomeRoute();
  const { newSession, clearChat } = useCopilotRuntime();
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && e.key === 'j')) return;
      // No typing guard: the dock focuses its composer on open, so one
      // would stop ⌘J from closing it. ⌘J can't be typed anyway.
      if (!hasCompletedOnboarding) return;
      e.preventDefault();

      if (onHomeRoute || onCopilotRoute) {
        posthog?.capture('copilot_surface_opened', {
          surface: 'full_screen',
          source: 'keyboard',
        });
        if (!newSession()) clearChat();
        dispatch(setAiChatInputValue(undefined));
        // The dock can't render here, so a stale open flag would pop it on
        // the next page that can.
        dispatch(setAiChatOpen(false));
        if (onHomeRoute) void navigate({ to: '/clu' });
        return;
      }

      // Capture only the open transition (toggle that reveals the dock).
      if (!aiChatOpen) {
        posthog?.capture('copilot_surface_opened', {
          surface: 'docked_sidebar',
          source: 'keyboard',
        });
      }
      dispatch(toggleAiChat());
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    aiChatOpen,
    clearChat,
    dispatch,
    hasCompletedOnboarding,
    navigate,
    newSession,
    onCopilotRoute,
    onHomeRoute,
    posthog,
  ]);

  return null;
}

/**
 * The reward bars: the AppSumo review reward, the Product Hunt reward
 * (non-AppSumo orgs) and the G2 review. Paused while the AppSumo upgrade bar
 * is up, so a workspace sees one bar at a time.
 */
function RewardBanners() {
  const { active: upgradeOfferShown } = useAppSumoUpgrade();
  if (upgradeOfferShown) return null;
  return (
    <>
      <ReviewRewardBanner />
      <ProductHuntRewardBanner />
      <G2ReviewBanner />
    </>
  );
}

/**
 * Measures the shell's offer bar into --app-banner-h, which the viewport-fixed
 * sidebars offset their top and height by (0px when no bar renders). A ref
 * callback, not a mount effect: the bar mounts a render after the shell
 * (CopilotRuntimeProvider renders nothing until it has stored the chat scope)
 * and again whenever that scope changes, so a mount effect found no element,
 * never measured, and left the sidebar over the trial bar.
 * Only a REAL height change dispatches the resize nudge, so the observer
 * can't feed back on itself through viewport-driven breakpoint changes.
 */
function measureOfferBar(el: HTMLDivElement | null) {
  if (!el) return;
  let lastHeight = -1;
  const update = () => {
    const height = el.getBoundingClientRect().height;
    if (height === lastHeight) return;
    lastHeight = height;
    document.documentElement.style.setProperty('--app-banner-h', `${height}px`);
    window.dispatchEvent(new Event('resize'));
  };
  update();
  const observer = new ResizeObserver(update);
  observer.observe(el);
  return () => {
    observer.disconnect();
    document.documentElement.style.removeProperty('--app-banner-h');
  };
}

/**
 * Persistent app shell that wraps the router outlet.
 * Mounts once at the root and never remounts on navigation,
 * so the AI sidebar, navigation sidebar, and overlays survive
 * route changes — preserving streaming state, scroll positions, etc.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const hasCompletedOnboarding = !!user?.unsafeMetadata?.completed_onboarding;

  // Boot Intercom Messenger for authenticated users
  useIntercomBoot();

  usePrefetchAiSidebar();

  return (
    // The appearance engine: paints the user's chrome colours.
    <AppearanceProvider>
      <SidebarProvider>
        <AppEffects />
        {/* One runtime, two presentations. The provider owns the single
          agent-chat runtime + AssistantRuntimeProvider and wraps both the
          router outlet (which renders /clu) and the docked AiSidebar, so
          they share one thread / one SSE stream. */}
        <CopilotRuntimeProvider>
          {/* The rep's browser call: one at a time, kept across pages, with
            its call bar. */}
          <CallProvider>
            <CopilotHotkey />
            <div className='flex h-screen flex-col overflow-hidden'>
              {/* Full-width offer bar ABOVE the nav sidebar and the page.
              The wrapper's measured height feeds --app-banner-h so the
              viewport-fixed sidebar starts below it; the content column
              shifts naturally because the bar is in-flow. The guarded resize
              dispatch re-runs AppLayout's drawer-inset measurement when the
              bar appears/dismisses (position shifts don't fire that wrapper's
              own ResizeObserver). */}
              <div ref={measureOfferBar} className='shrink-0'>
                {/* Trial status bar — renders only during an ACTIVE trial. */}
                <TrialStatusBanner />
                {/* AppSumo-only org while the Pro upgrade price is open. Never
                  beside a trial bar: an AppSumo org gets no trial. */}
                <AppSumoUpgradeBanner />
                {/* Free org with an open trial offer. */}
                <TrialOfferBanner />
                {/* The reward bars, across the nav and the page too. */}
                <RewardBanners />
              </div>
              <div className='flex min-h-0 flex-1 overflow-hidden'>
                {/* Phones only reach staff /management (see MobileBlocker),
                which has its own section picker: no nav rail or Clu dock. */}
                <div className='contents touch-only:hidden'>
                  <TabsSidebar />
                  {/* The docked Copilot panel sits on the LEFT (after the nav,
                  before the page content). */}
                  {/* A crash in the dock itself shows in the dock's own frame,
                  or nowhere when it is closed, so the page never moves. */}
                  <AgentChatErrorBoundary
                    name='dock'
                    fallback={(retry) => <AiSidebarFallback onRetry={retry} />}
                  >
                    <Suspense fallback={null}>
                      <AiSidebar />
                    </Suspense>
                  </AgentChatErrorBoundary>
                </div>
                {children}
              </div>
            </div>
            {/* Agents research finishes minutes after the user left the setup
            screen, so its result comes to them here — over whatever they
            moved on to. */}
            <AgentProfileReadyDialog />
          </CallProvider>
        </CopilotRuntimeProvider>

        {/* CommandPalette is mounted once at the router root (__root.tsx) so it
          works across every route with a single open-state + keydown listener.
          A second mount here double-opened the dialog on ⌘K and made the
          ⌘⇧L theme toggle fire twice (netting no change). */}
        {hasCompletedOnboarding && <InvitationPrompt />}
        {hasCompletedOnboarding && <TeamInvitePrompt />}
        <CreditGateDialog />
        {import.meta.env.DEV && AppSumoPreviewPanel ? (
          <Suspense fallback={null}>
            <AppSumoPreviewPanel />
          </Suspense>
        ) : null}
        <Suspense fallback={null}>
          <UpgradePromptHost />
        </Suspense>
        {/* Help & support lives in the sidebar footer — the old floating
          HelpFab was retired. */}
      </SidebarProvider>
    </AppearanceProvider>
  );
}
